import { useCallback, useEffect, useState } from 'react'
import type { MouseEvent as Mausereignis } from 'react'
import { createPortal } from 'react-dom'
import { bereiche, bereichsname, unterpunktname } from './bereiche'
import type { Bereich } from './bereiche'
import { Zeilenmenue } from './Zeilenmenue'
import type { Menuepunkt } from './Zeilenmenue'
import { t } from './sprache'
import type { Sprachschluessel } from './sprache'

/**
 * Ob es an diesem Gerät überhaupt ein zweites Fenster zum Nebeneinanderlegen
 * gibt (7.2 Abschnitt 3c, R531).
 *
 * **Wo es keines gibt, fängt die Leiste den Rechtsklick nicht ab.** Auf einem
 * Touchgerät und auf einem schmalen Schirm bleibt er, was er dort ist: das
 * Menü des Geräts. Ein Punkt, der nichts bewirkt, wird nicht angeboten
 * (R280) — und ihn anzubieten, indem man dem Anwender vorher das Menü des
 * Browsers wegnimmt, wäre der schlechtere Tausch von beiden.
 */
export function zweitfensterMoeglich(): boolean {
  const grob = window.matchMedia?.('(pointer: coarse)').matches ?? false
  return !grob && window.innerWidth >= 768
}

/** Was das Menü gerade betrifft — der Eintrag, sein Name und seine Adresse. */
type Menuestand = { x: number; y: number; name: string; adresse: string }

/**
 * Die Navigation (7.2, R150).
 *
 * Senkrecht und standardmäßig ausgeklappt, mit Symbol *und* Text. Der
 * Gelegenheitsanwender hat beim nächsten Quartal vergessen, wo er zuletzt
 * geklickt hat — zehn Symbole zu raten ist genau die Hürde, die 7.1 abschaffen
 * will. Wer den Platz braucht, klappt zu; der Zustand wird gemerkt.
 *
 * Drei Dinge sind nicht verhandelbar und stehen deshalb hier und nicht im
 * Stilblatt:
 *
 *  - Der **Tooltip ist Zusatz, nie einzige Beschriftung.** Er erscheint weder
 *    bei Tastaturbedienung noch auf Touch. Jeder Eintrag trägt zusätzlich ein
 *    `aria-label` und ein Tastenkürzel.
 *  - Der **aktive Bereich ist an drei Merkmalen** erkennbar — Balken, Fläche,
 *    Schriftstärke —, nicht an der Farbe allein (R143).
 *  - **Was der Anwender nicht darf, ist nicht da** (R155). Ausgegraute Einträge
 *    mit Schlosssymbol gibt es nicht: ein fehlendes Recht ist keine Aufgabe.
 */
export function Seitenleiste({
  aktiv,
  aktiverUnterpunkt,
  eingeklappt,
  aufAuswahl,
  aufKlappen,
  anhaenger,
  rechte,
  angemeldetAls,
  abgeschaltet,
  aufAbmelden,
  aufProfil,
  adresse,
}: {
  aktiv: string
  aktiverUnterpunkt: string | null
  eingeklappt: boolean
  aufAuswahl: (bereich: string, unterpunkt?: string) => void
  aufKlappen: (eingeklappt: boolean) => void
  /** Zahl am Bereichssymbol. Eine Menge ist keine Vermutung (R142). */
  anhaenger: Record<string, number>
  /** Die Verben des Anwenders (R358) — die Leiste lässt weg, was fehlt (R155). */
  rechte: readonly string[]
  angemeldetAls: string
  /** Die Anmeldung ist abgeschaltet (R365) — dann steht hier die Warnung. */
  abgeschaltet: boolean
  aufAbmelden: () => void
  /** Der Klick auf den eigenen Namen öffnet „Mein Profil" (7.7, R920). */
  aufProfil: () => void
  /**
   * Die Adresse eines Eintrags — **mit dem Kontext dieses Fensters** (R156,
   * R530).
   *
   * Sie wird nicht hier gebaut, sondern von der Schale gereicht: Kreis,
   * Bewertungsebene und Zeitraum liegen dort, und die Leiste soll von ihnen nichts
   * wissen müssen. Was sie damit tut, steht im Menü unten.
   */
  adresse: (bereich: string, unterpunkt?: string) => string
}) {
  // Alt+1 … Alt+0 in der Reihenfolge der Bereiche. Der Vielnutzer im Februar
  // braucht keine Maus.
  useEffect(() => {
    function beiTaste(e: KeyboardEvent) {
      if (!e.altKey || e.ctrlKey || e.metaKey) {
        return
      }

      const stelle = '1234567890'.indexOf(e.key)
      const ziel = stelle >= 0 ? bereiche[stelle] : undefined
      if (ziel) {
        e.preventDefault()
        aufAuswahl(ziel.id)
      }
    }

    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [aufAuswahl])

  /*
    **Das Kontextmenü der Leiste** (7.2 Abschnitt 3c, R529–R531).

    Es ist **dasselbe Bauteil wie das Menü an einer Berichtszeile** und nicht
    die zweite Abschrift — derselbe Grund wie beim Fuß der Leiste (R326):
    Zwei Menüs, die gleich aussehen sollen, sehen nach einem halben Jahr
    verschieden aus. Pfeiltasten, Escape und der Klick daneben kommen damit
    umsonst mit.

    **Drei Punkte, nicht einer.** Wer den Rechtsklick abfängt, nimmt dem
    Anwender, was der Browser dort anbot — also bringt er es mit: neues
    Fenster, neuer Tab, Adresse kopieren.

    **Die beiden Öffnen-Punkte sind Links, geöffnet werden sie aber von der
    Anwendung** (R550). Der Link trägt die Adresse — in der Statuszeile, im
    Kontextmenü des Browsers, unter dem Fokus wie ein Knopf; `window.open`
    macht das Fenster auf, weil ein Link es nicht kann. **Die eigene
    Navigation des Links ist abbestellt**: Sie wäre der einzige Weg, auf dem
    der Ort im Ausgangsfenster landen könnte — und ein Rechtsklick, der tut,
    was der Linksklick tut, ist der Befund vom 2026-09-15.
  */
  const [menue, setMenue] = useState<Menuestand | null>(null)

  const schliesse = useCallback(() => setMenue(null), [])

  function oeffneMenue(e: Mausereignis<HTMLElement>, name: string, bereich: string, unterpunkt?: string) {
    if (!zweitfensterMoeglich()) {
      return
    }

    e.preventDefault()

    /*
      Bei Tastaturbedienung öffnet das Menü **an der Zeile** (R166). Der
      Browser löst für die Kontextmenü-Taste dasselbe Ereignis aus wie für die
      Maus, meldet dabei aber keine Zeigerstelle — dann steht das Menü an der
      unteren rechten Ecke des Eintrags und nicht in der Bildschirmecke.
    */
    const rahmen = e.currentTarget.getBoundingClientRect()
    setMenue({
      x: e.clientX || rahmen.right,
      y: e.clientY || rahmen.bottom,
      name,
      adresse: adresse(bereich, unterpunkt),
    })
  }

  /** Aus der kurzen Adresse wird die, die man in eine Mail schreiben kann. */
  const volleAdresse = (kurz: string) => new URL(kurz, window.location.href).href

  function kopiereAdresse(kurz: string) {
    void navigator.clipboard?.writeText(volleAdresse(kurz))
  }

  /**
   * **Maße machen aus dem Tab ein Fenster.** Ohne sie öffnet der Browser einen
   * Tab, und ein Tab lässt sich nicht neben den ersten legen — was der einzige
   * Grund für diesen Punkt ist. Gibt der Browser das Fenster nicht her, öffnet
   * der Link denselben Ort als Tab: **ein Ort weniger nebeneinander, aber kein
   * Ort weniger.**
   */
  const fenstermasse = 'popup=yes,width=1280,height=900'

  const punkte: Menuepunkt[] = menue
    ? [
        {
          text: t('leiste.menue.fenster', { name: menue.name }),
          adresse: volleAdresse(menue.adresse),
          fenster: fenstermasse,
        },
        { text: t('leiste.menue.tab', { name: menue.name }), adresse: volleAdresse(menue.adresse) },
        { text: t('leiste.menue.adresse'), tun: () => kopiereAdresse(menue.adresse) },
      ]
    : []

  // **Der Fuß ist eine Stelle, keine zweite Sorte** (R326): Was dort steht,
  // ist ein Bereich wie jeder andere — gleiches Symbolfeld, gleiche
  // Beschriftung, gleiche Adresse. Getrennt wird nur, was *neben* dem
  // Arbeitsfluss liegt und deshalb nicht in seiner Reihe stehen soll.
  /*
    **R155 wird hier zum ersten Mal wirklich durchgesetzt:** Was ein Anwender
    nicht darf, ist **nicht da** — nicht ausgegraut, nicht mit Schlosssymbol.
    Bis zum 2026-09-11 war das eine Absichtserklärung, weil es keine Rechte
    gab; seit 8.1 gibt es sie.

    **Die Leiste schützt nichts** (R361). Sie lässt weg, was ins Leere führte;
    geprüft wird an der API, und ein Aufruf von Hand bekommt dort seine Absage.
  */
  const sichtbar = (b: Bereich) => !b.verb || rechte.includes(b.verb)

  const oben = bereiche.filter((b) => !b.fuss && sichtbar(b))
  const unten = bereiche.filter((b) => b.fuss && sichtbar(b))

  const zeile = (b: Bereich, gruppenkopf: Sprachschluessel | null) => (
    <Bereichszeile
      key={b.id}
      bereich={b}
      gruppenkopf={gruppenkopf}
      aktiv={aktiv}
      aktiverUnterpunkt={aktiverUnterpunkt}
      eingeklappt={eingeklappt}
      aufAuswahl={aufAuswahl}
      anhaenger={anhaenger}
      aufMenue={oeffneMenue}
    />
  )

  return (
    <nav className={`schiene ${eingeklappt ? 'eng' : ''}`} aria-label={t('leiste.bezeichnung')}>
      {/*
        **Der Klappknopf steht oben rechts im Kopf der Leiste** — dort, wo ihn
        jeder sucht, der ihn schon einmal woanders bedient hat (Auftrag vom
        2026-09-24).

        Bis dahin stand er ganz unten, in voller Breite und mit dem Wort
        „Einklappen" daneben. Das kostete eine Zeile für eine Bedienung, die
        man einmal am Tag braucht — und es stellte sie an eine Stelle, an der
        sie niemand vermutet: unter den Fußeinträgen, hinter Hilfe und
        Abmelden.

        **Eingeklappt bleibt nur der Knopf.** Auf sechzig Pixel Breite ist
        zwischen Wortmarke und Symbol nicht beides unterzubringen; was dann
        bliebe, wäre eine beschnittene Marke neben einem gequetschten Knopf.
        Die Marke kommt beim Ausklappen zurück — der Knopf muss immer
        greifbar sein, sonst käme sie nie wieder.
      */}
      <div className="marke">
        {/* „CoKo" ist der Produktname und wird nicht übersetzt. */}
        {!eingeklappt && <span className="wortmarke">CoKo</span>}
        {!eingeklappt && <span className="markenzusatz">{t('marke.zusatz')}</span>}

        <button
          type="button"
          className="klappknopf"
          onClick={() => aufKlappen(!eingeklappt)}
          aria-label={eingeklappt ? t('leiste.ausklappen.hilfe') : t('leiste.einklappen.hilfe')}
          aria-expanded={!eingeklappt}
        >
          {/*
            Zwei Balken mit einem Pfeil: das übliche Zeichen für eine Leiste,
            die zur Seite geht. Der Doppelwinkel allein sagte „weiter" und
            nicht „Leiste zu" — er steht in dieser Anwendung schon am
            Aufklapppfeil der Gliederung, und dasselbe Zeichen für zweierlei
            ist eines zu wenig.
          */}
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M9 4v16" />
            {eingeklappt ? <path d="M13.5 9.5l2.5 2.5-2.5 2.5" /> : <path d="M16.5 9.5L14 12l2.5 2.5" />}
          </svg>
          <span className="hinweisfahne" role="presentation">
            {eingeklappt ? t('leiste.ausklappen') : t('leiste.einklappen')}
          </span>
        </button>
      </div>

      <ul className="bereichsliste">
        {oben.map((b, i) =>
          // Die Überschrift steht an der Stelle, an der die Gruppe wechselt
          // (R324) — sie wird nicht wiederholt und nicht gezählt. Eingeklappt
          // bleibt von ihr die Trennlinie: Der Schnitt gilt weiter, nur der
          // Name hat keinen Platz.
          zeile(b, b.gruppe && b.gruppe !== oben[i - 1]?.gruppe ? b.gruppe : null),
        )}
      </ul>

      <ul className="bereichsliste fussliste">{unten.map((b) => zeile(b, null))}</ul>

      {/*
        **Wer man ist, steht dort, wo man aufhört** — im Fuß der Leiste, neben
        der Hilfe (R326): Das ist die Stelle für das, was *neben* dem
        Arbeitsfluss liegt. In die Fußzeile des Arbeitsfeldes gehört es nicht;
        die trägt Sprache, Zahlenformat und Prüfcode und hat eine feste Höhe
        (R336).
      */}
      {/*
        **Was hier steht, muss wahr sein.** Ist die Anmeldung abgeschaltet
        (R365), ist niemand angemeldet — dann steht hier die Warnung und kein
        Name, und es gibt nichts abzumelden. Ein Bildschirm, der in dieser
        Betriebsart „Angemeldet als Administrator" sagt, behauptet eine
        Anmeldung, die es nicht gibt; genau das hat R351 am Platzhalter
        verboten.
      */}
      <div className={`anmeldefuss ${abgeschaltet ? 'warnt' : ''}`}>
        {abgeschaltet ? (
          <>
            <span className="werbinich" title={t('anmeldung.abgeschaltet.hinweis')}>
              {eingeklappt ? '!' : t('anmeldung.abgeschaltet')}
            </span>
            {/*
              **Das Profil gibt es auch hier** (7.7, R920): Ohne Anmeldung gilt
              jeder Aufruf als „admin", und dessen Schalter ist ein Schalter
              wie jeder andere. Einen Namen trägt der Knopf nicht — angemeldet
              ist niemand.
            */}
            <button
              type="button"
              className="abmelden"
              onClick={aufProfil}
              aria-label={t('profil.titel')}
              title={t('profil.titel')}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21a8 8 0 0 1 16 0" />
              </svg>
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="werbinich"
              onClick={aufProfil}
              aria-label={t('profil.titel')}
              title={t('profil.titel')}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21a8 8 0 0 1 16 0" />
              </svg>
              {!eingeklappt && <span>{angemeldetAls}</span>}
            </button>
            <button
              type="button"
              className="abmelden"
              onClick={aufAbmelden}
              aria-label={t('anmeldung.abmelden')}
              title={t('anmeldung.abmelden')}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="M16 17l5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
              {!eingeklappt && <span>{t('anmeldung.abmelden')}</span>}
            </button>
          </>
        )}
      </div>

      {/*
        **Hier stand bis zum 2026-09-15 eine Meldung** — „der Browser hat das
        Fenster nicht geöffnet, lassen Sie Fenster für diese Adresse zu". Sie
        ist weg (R550): Wo ein Browser sperrt, sagt er es selbst in seiner
        Adresszeile, und was dort zu tun ist, ist seine Sache. Was die
        Anwendung an dieser Stelle schuldet, ist kein Satz, sondern ein
        Verhalten — **der Bildschirm, auf den der Anwender sieht, bleibt
        stehen**, und der Link bleibt greifbar.
      */}

      {/*
        **Das Menü hängt am Dokument und nicht in der Leiste.**

        Die Leiste ist 238 px breit und rollt in sich; das Menü ist breiter als
        sie, weil der Name des Eintrags darin steht. In ihr gezeichnet wäre es
        am Rand abgeschnitten — nachgemessen am 2026-09-14, und zwar bei
        gesetztem `position: fixed`: Was die Leiste nicht abschneidet,
        überdeckt das Arbeitsfeld daneben.
      */}
      {menue &&
        createPortal(
          <Zeilenmenue x={menue.x} y={menue.y} punkte={punkte} aufSchliessen={schliesse} />,
          document.body,
        )}

    </nav>
  )
}

/**
 * Ein Eintrag der Leiste — oben wie im Fuß derselbe.
 *
 * **Ein Bauteil und nicht zwei**: Der Fußeintrag soll aussehen und sich
 * verhalten wie die anderen, und das bleibt nur so, wenn es dieselbe Stelle
 * im Code ist (R326). Zwei Abschriften laufen mit der Zeit auseinander —
 * dieselbe Erfahrung wie bei den zwei Listen der gebauten Bildschirme.
 */
function Bereichszeile({
  bereich: b,
  gruppenkopf,
  aktiv,
  aktiverUnterpunkt,
  eingeklappt,
  aufAuswahl,
  anhaenger,
  aufMenue,
}: {
  bereich: Bereich
  gruppenkopf: Sprachschluessel | null
  aktiv: string
  aktiverUnterpunkt: string | null
  eingeklappt: boolean
  aufAuswahl: (bereich: string, unterpunkt?: string) => void
  anhaenger: Record<string, number>
  /**
   * Der Rechtsklick auf **einem Eintrag** (R529) — Bereich wie Unterpunkt.
   *
   * Er sitzt am Knopf und nicht an der Zeile: Auf der **Blocküberschrift**
   * darüber soll gerade nichts passieren. Sie ist eine Beschriftung und kein
   * Ort (R324), hat keine Adresse, und dort behält der Browser sein Menü.
   */
  aufMenue: (e: Mausereignis<HTMLElement>, name: string, bereich: string, unterpunkt?: string) => void
}) {
  const istAktiv = b.id === aktiv
  const Symbol = b.symbol
  const zahl = anhaenger[b.id]

  return (
    <li className={gruppenkopf ? 'gruppenanfang' : undefined}>
      {gruppenkopf && !eingeklappt && (
        <h2 className="bereichsgruppe">{t(gruppenkopf)}</h2>
      )}

      <button
        type="button"
        className={`bereichsknopf ${istAktiv ? 'aktiv' : ''}`}
        aria-current={istAktiv ? 'page' : undefined}
        /*
          Ohne Kürzel keine leere Klammer. Zwölf Bereiche und zehn Tasten
          (R325) lassen zwei ohne Kürzel — und ein Vorleseprogramm, das
          „Betrieb Klammer auf Klammer zu" sagt, ist schlechter als eines, das
          nur „Betrieb" sagt.
        */
        aria-label={b.kuerzel ? `${bereichsname(b)} (${b.kuerzel})` : bereichsname(b)}
        onClick={() => aufAuswahl(b.id)}
        onContextMenu={(e) => aufMenue(e, bereichsname(b), b.id)}
      >
        <span className="symbolfeld">
          <Symbol />
          {zahl !== undefined && zahl > 0 && (
            <span className="anhaenger" aria-hidden="true">
              {zahl > 99 ? '99+' : zahl}
            </span>
          )}
        </span>

        {!eingeklappt && <span className="bereichsname">{bereichsname(b)}</span>}

        {/*
          Der Tooltip trägt nichts allein: Das aria-label oben sagt dasselbe,
          und im ausgeklappten Zustand steht der Name ohnehin daneben. Er ist
          die Bequemlichkeit, nicht die Information.
        */}
        <span className="hinweisfahne" role="presentation">
          {bereichsname(b)}
          {b.kuerzel && <span className="kuerzel">{b.kuerzel}</span>}
        </span>
      </button>

      {/*
        Unterpunkte klappen im Bereich auf, nicht als überlagerndes Menü. Ein
        Menü, das sich beim Wegrutschen der Maus schließt, ist für acht
        Einträge unbenutzbar.
      */}
      {istAktiv && !eingeklappt && b.unterpunkte.length > 0 && (
        <ul className="unterpunkte">
          {b.unterpunkte.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                className={`unterknopf ${u.id === aktiverUnterpunkt ? 'aktiv' : ''}`}
                aria-current={u.id === aktiverUnterpunkt ? 'page' : undefined}
                onClick={() => aufAuswahl(b.id, u.id)}
                /*
                  **Gerade die Unterpunkte will man nebeneinander** — Bilanz
                  neben GuV, Klärungsliste neben Importieren. Ein Menü nur auf
                  den Bereichen öffnete zweimal denselben ersten Unterpunkt.
                */
                onContextMenu={(e) => aufMenue(e, unterpunktname(u), b.id, u.id)}
              >
                {unterpunktname(u)}
                {!u.gebaut && <span className="ungebaut">{t('leiste.geplant')}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}
